import {
  $createInteractionBody,
  $updateInteractionBody,
  CreateInteractionBody,
  InteractionAttributes,
  UpdateInteractionBody,
  interactionType$,
} from '@loyalty/shared-common/profile-api-client';
import { Button, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { Controller, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { Feature } from '@loyalty/shared-common';
import { QlfFormRow } from '@qualifio/ui-commons';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { DetailForm } from '../../../shared/layout/DetailForm';
import { DetailTemplateModal } from './DetailTemplateModal';
import { SuperAdminMarker } from '../../../shared/markers/SuperAdminMarker';
import { Title } from '../../../shared/layout/Title';
import { buildInteractionSaveBody } from '../../content-example';
import { getStarterDetailTemplate } from '../../starter-detail-templates';
import { useFeatures } from '../../../features/FeaturesContext';

export type InteractionFormValues = InteractionAttributes & {
  isNew?: boolean;
};

export interface InteractionFormProps {
  interaction: InteractionFormValues;
  onSubmit: (interaction: CreateInteractionBody | UpdateInteractionBody) => Promise<void>;
}

export function InteractionForm({ interaction, onSubmit }: InteractionFormProps) {
  const { t } = useTranslation();
  const { features } = useFeatures();
  const { write: interactionWrite } = features[Feature.INTERACTION];

  const resolver = zodResolver(interaction.isNew ? $createInteractionBody : $updateInteractionBody);

  const form = useForm<InteractionFormValues>({
    mode: 'onChange',
    resolver: resolver as any,
    defaultValues: interaction,
  });

  const {
    reset,
    handleSubmit,
    getValues,
    formState: { isDirty, isSubmitting, isValid },
  } = form;

  useEffect(() => {
    reset(interaction);
  }, [interaction, reset]);

  const forwardSubmit = useCallback(
    (value: InteractionFormValues) => {
      return onSubmit(buildInteractionSaveBody(value, getValues('type')));
    },
    [getValues, onSubmit],
  );

  return (
    <DetailForm>
      <FormProvider {...form}>
        <DetailForm.Form>
          <InteractionsFormFields isNew={!!interaction.isNew} readOnly={!interactionWrite} />
        </DetailForm.Form>
      </FormProvider>
      <DetailForm.Footer>
        <Tooltip
          title={interactionWrite ? '' : t('shared.action.permission.required')}
          placement="left"
        >
          <div>
            <Button
              color="green"
              onClick={handleSubmit(forwardSubmit, console.error)}
              disabled={
                !isValid || !(isDirty || interaction.isNew) || isSubmitting || !interactionWrite
              }
            >
              {t('generic.actions.save')}
            </Button>
          </div>
        </Tooltip>
      </DetailForm.Footer>
    </DetailForm>
  );
}

function InteractionsFormFields({ isNew, readOnly }: { isNew: boolean; readOnly: boolean }) {
  const { control, setValue } = useFormContext<InteractionFormValues>();
  const { t } = useTranslation();
  const type = useWatch({ control, name: 'type' });
  const contentExample = useWatch({ control, name: 'contentExample' });
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);

  useEffect(() => {
    if (isNew && type) {
      setValue('detailTemplate', getStarterDetailTemplate(type), {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [isNew, setValue, type]);

  return (
    <>
      <Title>
        {t('interactions.form.title')}
        <SuperAdminMarker />
      </Title>

      <Controller
        control={control}
        name="name"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <QlfFormRow label={t('interactions.inputs.name')}>
            <TextField
              {...field}
              inputRef={ref}
              placeholder={t('interactions.inputs.name-placeholder')}
              error={!!error}
              helperText={error?.message}
              required
            />
          </QlfFormRow>
        )}
      />

      <Controller
        control={control}
        name="type"
        render={({ field: { ref, ...field }, fieldState: { error } }) => (
          <QlfFormRow label={t('interactions.inputs.type')}>
            <TextField
              {...field}
              inputRef={ref}
              error={!!error}
              helperText={error?.message}
              select
              required
              disabled={!isNew}
            >
              {interactionType$.options.map(option => (
                <MenuItem key={option} value={option}>
                  {t(`interactions.types.${option}`)}
                </MenuItem>
              ))}
            </TextField>
          </QlfFormRow>
        )}
      />

      <Controller
        control={control}
        name="detailTemplate"
        render={({ field: { value }, fieldState: { error } }) => (
          <QlfFormRow label={t('interactions.inputs.detail-template')}>
            <Button
              variant="outlined"
              onClick={() => setIsTemplateModalOpen(true)}
              disabled={!type}
            >
              {t('interactions.inputs.edit-template')}
            </Button>
            {error?.message && (
              <Typography color="error" variant="body2" sx={{ marginTop: 1 }}>
                {error.message}
              </Typography>
            )}
            {type && (
              <DetailTemplateModal
                open={isTemplateModalOpen}
                type={type}
                template={value}
                contentExample={contentExample ?? null}
                readOnly={readOnly}
                onClose={() => setIsTemplateModalOpen(false)}
                onApply={(nextTemplate, nextContentExample) => {
                  setValue('detailTemplate', nextTemplate, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  setValue('contentExample', nextContentExample, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
              />
            )}
          </QlfFormRow>
        )}
      />
    </>
  );
}
